import { useEffect, useState } from 'react';

import { useMediaQuery } from '#src/app/shared/hooks/dom';

import { SWAP_MS } from './consts';

type SlotState = Readonly<{
	/** Icon index shown in each slot. */
	slots: readonly number[];
	/** The icon index the next swap brings in. */
	next: number;
	/** The slot the next swap replaces. */
	turn: number;
}>;

type Result = ModelHookResult<{ slots: readonly number[] }>;

function initialState(total: number, slotCount: number): SlotState {
	const visible = Math.min(total, slotCount);
	return { slots: Array.from({ length: visible }, (_, index) => index), next: visible % Math.max(total, 1), turn: 0 };
}

function swap(state: SlotState, total: number): SlotState {
	const slots = state.slots.map((iconIndex, slot) => (slot === state.turn ? state.next : iconIndex));
	return { slots, next: (state.next + 1) % total, turn: (state.turn + 1) % slots.length };
}

/**
 * Which of `total` icons sit in each of `slotCount` slots. Every `SWAP_MS` one slot, round-robin,
 * takes the next icon not on screen, so no two slots ever match. Static when every icon fits or
 * under `prefers-reduced-motion`.
 */
export function useCyclingIconSlots(total: number, slotCount: number): Result {
	const motionReduced = useMediaQuery('(prefers-reduced-motion: reduce)');
	const [state, setState] = useState<SlotState>(() => initialState(total, slotCount));
	const [filledFor, setFilledFor] = useState({ total, slotCount });

	// the list changed: start over, in this render too
	const stale = filledFor.total !== total || filledFor.slotCount !== slotCount;
	if (stale) {
		setFilledFor({ total, slotCount });
		setState(initialState(total, slotCount));
	}
	const slots = stale ? initialState(total, slotCount).slots : state.slots;

	const cycles = total > slotCount && !motionReduced;

	useEffect(() => {
		if (!cycles) return undefined;

		const timer = setInterval(() => setState((current) => swap(current, total)), SWAP_MS);

		return () => clearInterval(timer);
	}, [cycles, total]);

	return { data: { slots } };
}
